import React, { useState } from 'react';
import { Box, Typography, TextField, Button, Grid, Paper, Avatar } from '@mui/material';
import { styled } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';

const ProfilePaper = styled(Paper)({
  padding: '2rem',
  backgroundColor: '#fafafa',
  border: '1px solid #eee',
});

const SectionTitle = styled(Typography)({
  fontFamily: 'Cormorant Garamond, serif',
  fontSize: '1.8rem',
  color: '#1a1a1a',
  marginBottom: '1.5rem',
});

const StyledButton = styled(Button)({
  backgroundColor: '#1a1a1a',
  color: 'white',
  padding: '0.8rem 2rem',
  '&:hover': {
    backgroundColor: '#c4a661',
  },
});

const StyledAvatar = styled(Avatar)({
  width: 120,
  height: 120,
  marginBottom: '1rem',
});

const Profile = () => {
  // Mock data - replace with real data from backend
  const [profile, setProfile] = useState({
    firstName: 'Jane',
    lastName: 'Smith',
    email: 'jane.smith@example.com',
    phone: '(555) 123-4567',
    instagram: '@janesmith',
    tiktok: '@janesmith',
    youtube: 'youtube.com/janesmith',
    website: 'janesmith.com',
    bio: 'Fashion and lifestyle content creator with a passion for luxury jewelry. Sharing my favorite pieces and styling tips with my amazing community.',
    profileImage: '/path/to/profile-image.jpg',
  });

  const [isEditing, setIsEditing] = useState(false);
  const [editedProfile, setEditedProfile] = useState(profile);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditedProfile(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    try {
      // TODO: Save profile changes to backend
      setProfile(editedProfile);
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving profile:', error);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // TODO: Upload image to storage and update profile
      const reader = new FileReader();
      reader.onload = () => {
        setEditedProfile(prev => ({
          ...prev,
          profileImage: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <ProfilePaper elevation={0}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <SectionTitle>Profile Information</SectionTitle>
        <StyledButton
          startIcon={isEditing ? <SaveIcon /> : <EditIcon />}
          onClick={isEditing ? handleSave : () => setIsEditing(true)}
        >
          {isEditing ? 'Save Changes' : 'Edit Profile'}
        </StyledButton>
      </Box>

      <Grid container spacing={4}>
        <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
          <Box>
            <StyledAvatar src={editedProfile.profileImage} />
            {isEditing && (
              <Button
                component="label"
                variant="outlined"
                sx={{ mt: 2 }}
              >
                Upload Photo
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleImageUpload}
                />
              </Button>
            )}
          </Box>
        </Grid>

        <Grid item xs={12} md={8}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                name="firstName"
                value={editedProfile.firstName}
                onChange={handleChange}
                disabled={!isEditing}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                name="lastName"
                value={editedProfile.lastName}
                onChange={handleChange}
                disabled={!isEditing}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={editedProfile.email}
                onChange={handleChange}
                disabled={!isEditing}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                name="phone"
                value={editedProfile.phone}
                onChange={handleChange}
                disabled={!isEditing}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Instagram"
                name="instagram"
                value={editedProfile.instagram}
                onChange={handleChange}
                disabled={!isEditing}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="TikTok"
                name="tiktok"
                value={editedProfile.tiktok}
                onChange={handleChange}
                disabled={!isEditing}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="YouTube"
                name="youtube"
                value={editedProfile.youtube}
                onChange={handleChange}
                disabled={!isEditing}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Website"
                name="website"
                value={editedProfile.website}
                onChange={handleChange}
                disabled={!isEditing}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Bio"
                name="bio"
                value={editedProfile.bio}
                onChange={handleChange}
                disabled={!isEditing}
                multiline
                rows={4}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </ProfilePaper>
  );
};

export default Profile;
